import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientService } from '../services/clientService';
import { ClientOnboardingFormData, UpdateClientData } from '../types';
import { queryKeys } from '../lib/queryClient';

export const useCreateClientWithOnboarding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: ClientOnboardingFormData) => {
      return await clientService.createClientWithOnboarding(formData);
    },
    onSuccess: () => {
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
    },
  });
};

export const useClientDetails = (id: number) => {
  return useQuery({
    queryKey: queryKeys.clients.detail(id.toString()),
    queryFn: () => clientService.getClient(id),
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateClientData }) => {
      return await clientService.updateClient(id, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate clients list and specific client detail
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(variables.id.toString()) });
    },
  });
};

export const useToggleClientStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: 'active' | 'inactive' }) => {
      return await clientService.updateClient(id, { status });
    },
    onSuccess: () => {
      // Invalidate clients list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
    },
  });
};